// frontend/src/pages/admin/POS.js
import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiUser,
  FiX,
  FiPrinter,
  FiDollarSign,
  FiPackage
} from 'react-icons/fi';

const POS = () => {
  // State management
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [barcodeInput, setBarcodeInput] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [cartItems, setCartItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);

  const [loading, setLoading] = useState(false);

  // ✅ POS Settings from database
  const [posSettings, setPosSettings] = useState({
    pos_enable_vat: '1',
    pos_vat_rate: '10',
    pos_receipt_format: 'thermal',
    pos_auto_print: '0'
  });

  // Refs for auto-focus
  const barcodeInputRef = useRef(null);
  const productSearchRef = useRef(null);
  const customerSearchRef = useRef(null);

  // ========================================
  // LOAD POS SETTINGS ON MOUNT
  // ========================================
  useEffect(() => {
    fetchPOSSettings();
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  const fetchPOSSettings = async () => {
    try {
      const { data } = await axios.get('/api/pos/settings');
      if (data.success) {
        setPosSettings(data.data.settings);
      }
    } catch (error) {
      console.error('Fetch POS settings error:', error);
    }
  };

  // ========================================
  // CUSTOMER SEARCH
  // ========================================
  const handleCustomerSearch = async (query) => {
    setCustomerSearchQuery(query);

    if (query.length < 2) {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
      return;
    }

    try {
      const response = await axios.get(`/api/pos/customers/search?query=${query}`);
      if (response.data.success) {
        setCustomerSearchResults(response.data.data.customers);
        setShowCustomerDropdown(true);
      }
    } catch (error) {
      console.error('Customer search error:', error);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
  };

  // ========================================
  // BARCODE SCAN
  // ========================================
  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;

    try {
      const response = await axios.get(`/api/pos/products/barcode/${barcodeInput}`);
      if (response.data.success) {
        addToCart(response.data.data.product);
        setBarcodeInput('');
        toast.success('Бараа нэмэгдлээ');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Бараа олдсонгүй');
      setBarcodeInput('');
    }
  };

  // ========================================
  // PRODUCT SEARCH
  // ========================================
  const handleProductSearch = async (query) => {
    setProductSearchQuery(query);

    if (query.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      return;
    }

    try {
      const response = await axios.get(`/api/pos/products/search?query=${query}`);
      if (response.data.success) {
        setProductSearchResults(response.data.data.products);
        setShowProductDropdown(true);
      }
    } catch (error) {
      console.error('Product search error:', error);
    }
  };

  const selectProduct = (product) => {
    addToCart(product);
    setProductSearchQuery('');
    setProductSearchResults([]);
    setShowProductDropdown(false);
  };

  // ========================================
  // CART MANAGEMENT
  // ========================================
  const addToCart = (product) => {
    const existingItem = cartItems.find(item => item.id === product.id);
    
    if (existingItem) {
      updateQuantity(product.id, existingItem.quantity + 1);
    } else {
      const initialPrice = 1 >= product.wholesale_quantity 
        ? product.wholesale_price 
        : product.retail_price;
        
      setCartItems([...cartItems, {
        ...product,
        quantity: 1,
        unit_price: initialPrice
      }]);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems(cartItems.map(item => {
      if (item.id === productId) {
        if (newQuantity > item.stock_quantity) {
          toast.warning(`Хамгийн ихдээ ${item.stock_quantity} ширхэг байна`);
          return item;
        }
        
        // Determine price based on quantity
        let newUnitPrice;
        if (newQuantity >= item.wholesale_quantity) {
          newUnitPrice = item.wholesale_price;
          
          if (item.unit_price !== item.wholesale_price) {
            toast.success(`🎉 Бөөний үнэ идэвхжлээ! ${item.wholesale_price.toLocaleString()}₮`);
          }
        } else {
          newUnitPrice = item.retail_price;
        }
        
        return { 
          ...item, 
          quantity: newQuantity,
          unit_price: newUnitPrice
        };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCartItems(cartItems.filter(item => item.id !== productId));
  };

  const clearCart = () => {
    setCartItems([]);
    setDiscount(0);
    setPaidAmount(0);
    setNotes('');
    clearCustomer();
  };

  // ========================================
  // CALCULATIONS WITH VAT
  // ========================================
  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  const calculateVAT = () => {
    if (posSettings.pos_enable_vat === '1') {
      const vatRate = parseFloat(posSettings.pos_vat_rate) || 10;
      return (calculateSubtotal() * vatRate) / 100;
    }
    return 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT() - discount;
  };

  const calculateChange = () => {
    return paidAmount - calculateTotal();
  };

  // ========================================
  // PAYMENT
  // ========================================
  const openPaymentModal = () => {
    if (cartItems.length === 0) {
      toast.warning('Бараа сонгоно уу');
      return;
    }
    setShowPaymentModal(true);
    setPaidAmount(calculateTotal());
  };

  const handlePayment = async () => {
    if (paidAmount < calculateTotal()) {
      toast.error('Төлсөн дүн хүрэлцэхгүй байна');
      return;
    }

    setLoading(true);

    try {
      const invoiceData = {
        customer_id: selectedCustomer?.id || null,
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        subtotal: calculateSubtotal(),
        vat_amount: calculateVAT(),
        total_amount: calculateSubtotal() + calculateVAT(),
        discount_amount: discount,
        paid_amount: paidAmount,
        change_amount: calculateChange(),
        payment_method: paymentMethod,
        notes: notes
      };

      const response = await axios.post('/api/pos/invoices', invoiceData);

      if (response.data.success) {
        toast.success('Борлуулалт амжилттай!');
        setLastInvoice(response.data.data.invoice);
        setShowPaymentModal(false);
        
        // Auto print if enabled
        if (posSettings.pos_auto_print === '1') {
          handlePrint(posSettings.pos_receipt_format);
        } else {
          setShowPrintModal(true);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (format) => {
    if (!lastInvoice) return;

    // Open print window
    window.open(`/print-receipt/${lastInvoice.id}?format=${format}`, '_blank', 'width=800,height=600');
    
    // Clear cart after a short delay
    setTimeout(() => {
      clearCart();
      setShowPrintModal(false);
      setLastInvoice(null);
    }, 500);
  };

  const skipPrint = () => {
    clearCart();
    setShowPrintModal(false);
    setLastInvoice(null);
  };

  // ========================================
  // KEYBOARD SHORTCUTS
  // ========================================
  useEffect(() => {
  const handleKeyPress = (e) => {
    // Payment Modal keyboard shortcuts
    if (showPaymentModal) {
      if (e.key === 'Enter' && paidAmount >= calculateTotal() && !loading) {
        e.preventDefault();
        handlePayment();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowPaymentModal(false);
      }
      return; // Don't process other shortcuts when modal is open
    }

    // Print Modal keyboard shortcuts
    if (showPrintModal) {
      if (e.key === 'F5') {
        e.preventDefault();
        handlePrint('a5');
      }
      if (e.key === 'F8') {
        e.preventDefault();
        handlePrint('thermal');
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        skipPrint();
      }
      return; // Don't process other shortcuts when modal is open
    }

    // Main POS shortcuts (when no modal is open)
    if (e.key === 'F1') {
      e.preventDefault();
      barcodeInputRef.current?.focus();
    }
    if (e.key === 'F2') {
      e.preventDefault();
      productSearchRef.current?.focus();
    }
    if (e.key === 'F3') {
      e.preventDefault();
      customerSearchRef.current?.focus();
    }
    if (e.key === 'F9') {
      e.preventDefault();
      openPaymentModal();
    }
    if (e.key === 'Escape') {
      clearCart();
    }
  };

  window.addEventListener('keydown', handleKeyPress);
  return () => window.removeEventListener('keydown', handleKeyPress);
}, [cartItems, showPaymentModal, showPrintModal, paidAmount, loading]);

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="pos-container">
      {/* Header */}
      <div className="pos-header">
        <h1 className="text-2xl font-bold text-gray-900">
          <FiShoppingCart className="inline mr-2" />
          Борлуулалт (POS)
        </h1>
        <div className="text-sm text-gray-600">
          <kbd>F1</kbd> Баркод | <kbd>F2</kbd> Хайх | <kbd>F3</kbd> Харилцагч | <kbd>F9</kbd> Төлбөр | <kbd>F8</kbd> 80mm | <kbd>F5</kbd> A5 | <kbd>ESC</kbd> Цэвэрлэх
          {posSettings.pos_enable_vat === '1' && (
            <span className="ml-4 text-green-600 font-medium">✓ НӨАТ {posSettings.pos_vat_rate}%</span>
          )}
        </div>
      </div>

      <div className="pos-grid">
        {/* LEFT SIDE - Products */}
        <div className="pos-left">
          {/* Customer Selection */}
          <div className="card mb-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">
                <FiUser className="inline mr-1" />
                Харилцагч (заавал биш)
              </label>
              {selectedCustomer && (
                <button
                  onClick={clearCustomer}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  <FiX className="inline" /> Арилгах
                </button>
              )}
            </div>

            {selectedCustomer ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-semibold text-gray-900">{selectedCustomer.full_name}</p>
                <p className="text-sm text-gray-600">{selectedCustomer.phone}</p>
                {selectedCustomer.address && (
                  <p className="text-xs text-gray-500 mt-1">{selectedCustomer.address}</p>
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={customerSearchRef}
                  type="text"
                  value={customerSearchQuery}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  placeholder="Нэр, утас... (F3) - Заавал биш"
                  className="input"
                />
                {showCustomerDropdown && customerSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {customerSearchResults.map(customer => (
                      <div
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                      >
                        <p className="font-semibold text-gray-900">{customer.full_name}</p>
                        <p className="text-sm text-gray-600">{customer.phone}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Barcode Scanner */}
          <div className="card mb-4">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              <FiPackage className="inline mr-1" />
              Баркод уншуулах (F1)
            </label>
            <form onSubmit={handleBarcodeSubmit}>
              <input
                ref={barcodeInputRef}
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="Баркод уншуулах..."
                className="input"
                autoComplete="off"
              />
            </form>
          </div>

          {/* Product Search */}
          <div className="card mb-4">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              <FiSearch className="inline mr-1" />
              Бараа хайх (F2)
            </label>
            <div className="relative">
              <input
                ref={productSearchRef}
                type="text"
                value={productSearchQuery}
                onChange={(e) => handleProductSearch(e.target.value)}
                placeholder="Барааны нэр..."
                className="input"
                autoComplete="off"
              />
              {showProductDropdown && productSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  {productSearchResults.map(product => (
                    <div
                      key={product.id}
                      onClick={() => selectProduct(product)}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-0 flex items-center"
                    >
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded mr-3"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded mr-3 flex items-center justify-center">
                          <FiPackage className="text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-600">
                          {product.barcode} | Үлдэгдэл: {product.stock_quantity}
                        </p>
                        <p className="text-sm font-bold text-blue-600">
                          {product.retail_price.toLocaleString()}₮
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Cart */}
        <div className="pos-right">
          <div className="card h-full flex flex-col">
            {/* Cart Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <h3 className="text-lg font-bold text-gray-900">
                Сагс ({cartItems.length})
              </h3>
              {cartItems.length > 0 && (
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  <FiTrash2 className="inline mr-1" />
                  Цэвэрлэх
                </button>
              )}
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto mb-4">
              {cartItems.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <FiShoppingCart size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Сагс хоосон байна</p>
                  <p className="text-sm mt-2">Бараа нэмнэ үү</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cartItems.map(item => (
                    <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.barcode}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-600 hover:text-red-700 ml-2"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100"
                          >
                            <FiMinus size={14} />
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                            className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                            min="1"
                            max={item.stock_quantity}
                          />
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {item.unit_price.toLocaleString()}₮ × {item.quantity}
                          </p>
                          <p className="font-bold text-blue-600">
                            {(item.unit_price * item.quantity).toLocaleString()}₮
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Summary */}
            {cartItems.length > 0 && (
              <>
                <div className="border-t pt-4 space-y-2">
                  {/* Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Дэд дүн:</span>
                    <span className="font-semibold">{calculateSubtotal().toLocaleString()}₮</span>
                  </div>

                  {/* VAT */}
                  {posSettings.pos_enable_vat === '1' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">НӨАТ ({posSettings.pos_vat_rate}%):</span>
                      <span className="font-semibold text-green-600">{calculateVAT().toLocaleString()}₮</span>
                    </div>
                  )}

                  {/* Discount */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Хөнгөлөлт:</span>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      className="w-32 text-right border border-gray-300 rounded px-2 py-1 text-sm"
                      min="0"
                      max={calculateSubtotal() + calculateVAT()}
                    />
                  </div>

                  {/* Total */}
                  <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                    <span>Нийт:</span>
                    <span className="text-blue-600">{calculateTotal().toLocaleString()}₮</span>
                  </div>
                </div>

                {/* Payment Button */}
                <button
                  onClick={openPaymentModal}
                  className="btn btn-primary w-full mt-4 py-3 text-lg"
                >
                  <FiDollarSign className="inline mr-2" />
                  Төлбөр (F9)
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Төлбөр тооцоолох</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Total */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">Төлөх дүн:</p>
                <p className="text-3xl font-bold text-blue-600">
                  {calculateTotal().toLocaleString()}₮
                </p>
                {posSettings.pos_enable_vat === '1' && (
                  <p className="text-xs text-gray-500 mt-1">
                    (НӨАТ {posSettings.pos_vat_rate}% орсон: {calculateVAT().toLocaleString()}₮)
                  </p>
                )}
              </div>

              {/* Payment Method */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Төлбөрийн хэлбэр
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="input"
                >
                  <option value="cash">Бэлэн мөнгө</option>
                  <option value="card">Карт</option>
                  <option value="transfer">Шилжүүлэг</option>
                  <option value="credit">Зээл</option>
                </select>
              </div>

              {/* Paid Amount */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Төлсөн дүн
                </label>
                <input
                  type="number"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                  className="input text-lg font-semibold"
                  min={calculateTotal()}
                  autoFocus
                />
              </div>

              {/* Change */}
              {paidAmount >= calculateTotal() && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">Хариу:</p>
                  <p className="text-2xl font-bold text-green-600">
                    {calculateChange().toLocaleString()}₮
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Тэмдэглэл
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input"
                  rows="2"
                  placeholder="Тэмдэглэл..."
                ></textarea>
              </div>
            </div>

            <div className="modal-footer">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="btn btn-secondary"
            >
              Буцах <kbd className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">ESC</kbd>
            </button>
            <button
              onClick={handlePayment}
              disabled={loading || paidAmount < calculateTotal()}
              className="btn btn-success"
            >
              {loading ? (
                <>
                  <div className="spinner mr-2"></div>
                  Боловсруулж байна...
                </>
              ) : (
                <>
                  Төлбөр хийх <kbd className="ml-2 px-2 py-1 text-xs bg-white text-green-700 rounded">ENTER</kbd>
                </>
              )}
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && lastInvoice && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Баримт хэвлэх</h3>
            </div>

            <div className="modal-body">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center">
                <p className="text-sm text-gray-600 mb-1">Борлуулалт амжилттай!</p>
                <p className="text-2xl font-bold text-green-600">
                  {lastInvoice.invoice_number}
                </p>
                <p className="text-lg font-semibold text-gray-900 mt-2">
                  {lastInvoice.total_amount.toLocaleString()}₮
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4 text-center">
                Баримт хэвлэх үү?
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handlePrint('thermal')}
                  className="btn btn-primary w-full justify-center"
                >
                  <FiPrinter className="mr-2" />
                  80mm Баримт <kbd className="ml-2 px-2 py-1 text-xs bg-white text-blue-700 rounded">F8</kbd>
                </button>
                <button
                  onClick={() => handlePrint('a5')}
                  className="btn btn-outline w-full justify-center"
                >
                  <FiPrinter className="mr-2" />
                  A5 Баримт <kbd className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">F5</kbd>
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={skipPrint}
                className="btn btn-secondary w-full justify-center"
              >
                Хэвлэхгүй <kbd className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">ESC</kbd>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;